"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { InfiniteScroll } from "@/components/core/InfiniteScroll/InfiniteScroll";
import { LoginRequiredModal } from "@/components/core/LoginRequiredModal";
import { useAuth } from "@/context/auth";
import { useFetchBlogComments } from "@/hooks/blog/useFetchBlogComments";
import { hasLoadedListItems } from "@/lib/pagination/hasLoadedListItems";
import { CommentForm } from "./CommentForm";
import { CommentItem, type CommentNode } from "./CommentItem";
import { CommentListSkeleton } from "./CommentListSkeleton";

const FA = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const faNum = (n: number) => String(n).replace(/[0-9]/g, (d) => FA[Number(d)]);

export const BlogComments: React.FC<{ slug: string }> = ({ slug }) => {
  const t = useTranslations("blog");
  const isLoggedIn = useAuth((s) => s.isLoggedIn);
  const [loginOpen, setLoginOpen] = useState(false);
  const query = useFetchBlogComments(slug);

  const listKey = ["blog-comments", slug, 20];

  const items: CommentNode[] =
    query.data?.pages.flatMap((p) => p?.data?.result ?? []) ?? [];
  const total =
    (query.data?.pages[0]?.data?.summary?.total_count as number | undefined) ??
    0;

  return (
    <section className="mt-8 border-t border-stroke-light/40 pt-6">
      <h2 className="mb-6 text-lg font-bold text-text-primary">
        {t("comments.heading")} ({faNum(total)})
      </h2>

      <CommentForm
        slug={slug}
        isLoggedIn={isLoggedIn}
        onRequireLogin={() => setLoginOpen(true)}
      />

      <div className="mt-8 space-y-6">
        {query.isLoading && !hasLoadedListItems(query) ? (
          <CommentListSkeleton />
        ) : (
          items.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              slug={slug}
              listKey={listKey}
              isLoggedIn={isLoggedIn}
              onRequireLogin={() => setLoginOpen(true)}
            />
          ))
        )}
        <InfiniteScroll query={query} />
      </div>

      <LoginRequiredModal open={loginOpen} onOpenChange={setLoginOpen} />
    </section>
  );
};
